import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Pencil, X, Paintbrush, Copy } from 'lucide-react';
import type {
  LayoutStyle,
  PanfletoFieldStyle,
  PanfletoPriceCaptionLayout,
  Promotion,
  PanfletoDraggableElementId,
  PanfletoElementPlacements,
  PanfletoStyleFieldKey,
  PanfletoTextOuterShadowDirection,
} from '../../../shared/types';
import {
  adaptPanfletoFieldVisualStyleForTarget,
  buildPanfletoTextFieldBorderEffectStyle,
  buildPanfletoTextOuterShadowCss,
  getDefaultPanfletoElementPlacementsForProductShowcase,
  getPanfletoFieldBase,
  mergePanfletoFieldStyle,
  mergePanfletoPricePairStyle,
  isPanfletoPriceSplitOn,
  normalizeHexColorString,
  PANFLETO_IMAGEM_MIN_HEIGHT_PX,
  PANFLETO_IMAGEM_MIN_WIDTH_PX,
  PANFLETO_STYLE_FIELD_LABELS,
  panfletoFieldIsHidden,
  resolvePanfletoDisplayTexts,
  resolvePanfletoProductNameAlign,
  snapshotPanfletoFieldVisualStyle,
} from '../../banners/utils/panfletoContentUtils';
import { useSimulatedViewport } from '../../display/context/SimulationFrameContext';

function getOwnerDocument(el: HTMLElement | null | undefined): Document {
  return el?.ownerDocument ?? document;
}

function getOwnerWindow(el: HTMLElement | null | undefined): Window {
  return el?.ownerDocument?.defaultView ?? window;
}

type TextStyleKey = keyof Omit<LayoutStyle, 'productContainer' | 'priceBox' | 'panfletoContent'>;
const FIELD_BY_ELEMENT_ID: Partial<Record<PanfletoDraggableElementId, PanfletoStyleFieldKey>> = {
  texto1: 'texto1',
  texto2: 'texto2',
  texto3: 'texto3',
  linha1: 'linha1',
  linha2: 'linha2',
  precoDe: 'precoDe',
  precoPorValor: 'precoPor',
  quantidade: 'quantidade',
  validade: 'validade',
};
const PRODUCT_PANEL_ELEMENT_IDS: PanfletoDraggableElementId[] = ['imagemProduto'];

function isProductPanelElement(id: PanfletoDraggableElementId): boolean {
  return PRODUCT_PANEL_ELEMENT_IDS.includes(id);
}

function getShowcaseDefaultPlacement(id: PanfletoDraggableElementId): {
  xPct: number;
  yPct: number;
  widthPx?: number;
  heightPx?: number;
} {
  const byId = getDefaultPanfletoElementPlacementsForProductShowcase();
  return byId[id] ?? { xPct: 50, yPct: 50 };
}

function formatBrazilianNumber(value: number | undefined, showCurrency: boolean = true): string {
  const numValue = value ?? 0;
  const formattedNumber = numValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return showCurrency ? `R$ ${formattedNumber}` : formattedNumber;
}

function clampPct(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

/** Valor válido para `<input type="color">` (#rrggbb). */
function hexForPanfletoFieldColor(value: string | undefined, fallback: string): string {
  const n = normalizeHexColorString(value ?? '');
  if (n) return n;
  const f = normalizeHexColorString(fallback);
  return f || '#ffffff';
}

/** Tamanho em px para o input; `fontSize=0` (oculto) mostra 1 no controle. */
function panfletoFieldFontSizeForInput(ov: PanfletoFieldStyle | undefined, base: { fontSize: number }): number {
  if (ov?.fontSize !== undefined) {
    if (ov.fontSize === 0) return 1;
    if (ov.fontSize > 0) return ov.fontSize;
  }
  return base.fontSize;
}

const panfletoFieldCustomizerColorInputClass =
  'h-8 w-11 shrink-0 cursor-pointer overflow-hidden rounded border border-gray-300 p-0 [color-scheme:light] [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch-wrapper]:rounded [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:border-0';

function pricePairInnerRowClass(split: boolean, layout: PanfletoPriceCaptionLayout | undefined, anim: string): string {
  const tail = `px-1 py-2 ${anim}`;
  if (!split) return `flex flex-col items-center ${tail}`;
  if (layout === 'left') return `flex flex-row flex-wrap items-baseline justify-center gap-x-2 ${tail}`;
  return `flex flex-col items-center ${tail}`;
}

export interface PanfletoFlyerContentProps {
  layoutStyle?: LayoutStyle;
  promo: Promotion;
  density: 'display' | 'preview';
  panfletoProdutoFotoViaApi?: boolean;
  panfletoFotoObjectUrl?: string | null;
  useFreeformLayout: boolean;
  /** Usado no layout livre (valores completos recomendados). */
  elementPlacements?: PanfletoElementPlacements;
  positionEditMode?: boolean;
  onElementPlacementsChange?: (next: PanfletoElementPlacements) => void;
  onFieldStyleChange?: (
    field: PanfletoStyleFieldKey,
    patch: Partial<PanfletoFieldStyle>,
    options?: { replace?: boolean }
  ) => void;
  /** Captura inicial a partir do layout real já renderizado (sem chute). */
  onInitializeFromCurrentLayout?: (next: PanfletoElementPlacements) => void;
  getTextStyle: (styleKey: TextStyleKey) => React.CSSProperties;
  /** Quando true, usa altura do frame pai (sem min-h em vh). Útil para preview 1920x1080 escalado. */
  fixedFrameMode?: boolean;
  /**
   * Se a tela tem ao menos um produto vinculado (lista/API).
   * `false`: sem vitrine split; **não** exibe textos do produto (nome, categoria, preços, qtd), mesmo com `promo` de exemplo no preview.
   * Omitir = só `hasRealProduct` a partir do `promo`.
   */
  panfletoHasProductsOnScreen?: boolean;
}

export const PanfletoFlyerContent: React.FC<PanfletoFlyerContentProps> = ({
  layoutStyle,
  promo,
  density,
  panfletoProdutoFotoViaApi = false,
  panfletoFotoObjectUrl = null,
  useFreeformLayout,
  elementPlacements = {},
  positionEditMode = false,
  onElementPlacementsChange,
  onFieldStyleChange,
  onInitializeFromCurrentLayout,
  getTextStyle,
  fixedFrameMode = false,
  panfletoHasProductsOnScreen,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  /** Bbox real dos painéis da vitrine (tela) — limite de arraste = o que está desenhado, não % teórico. */
  const showcaseTextPaneRef = useRef<HTMLDivElement>(null);
  const showcaseProductPaneRef = useRef<HTMLDivElement>(null);
  const stackMeasureRootRef = useRef<HTMLDivElement>(null);
  const initFromDomDoneRef = useRef(false);
  const dragRef = useRef<{
    kind: 'move' | 'resize-image';
    id: PanfletoDraggableElementId;
    pointerId: number;
    startClientX: number;
    startClientY: number;
    /** Distância do clique ao centro salvo (%). Evita o bloco “pular” para o cursor no 1º move. */
    grabOffsetXPct: number;
    grabOffsetYPct: number;
    startPlacement: { xPct: number; yPct: number; widthPx?: number; heightPx?: number };
    resizeAxis?: { x: -1 | 0 | 1; y: -1 | 0 | 1 };
    /** Só aplica patch depois de mover um pouco (ruído de pointer / setPointerCapture). */
    hasMoved: boolean;
  } | null>(null);
  const DRAG_MOVE_THRESHOLD_PX = 3;
  /** Evita `patchPlacement` depender de `elementPlacements` (e recriar listeners do window a cada frame). */
  const elementPlacementsRef = useRef(elementPlacements);
  elementPlacementsRef.current = elementPlacements;
  const prevPromoIdRef = useRef<number | null>(null);
  const [editingBorderField, setEditingBorderField] = useState<PanfletoStyleFieldKey | null>(null);
  const editingBorderFieldRef = useRef<PanfletoStyleFieldKey | null>(null);
  editingBorderFieldRef.current = editingBorderField;
  const [styleClipboard, setStyleClipboard] = useState<{
    sourceField: PanfletoStyleFieldKey;
    style: PanfletoFieldStyle;
  } | null>(null);
  const styleClipboardRef = useRef(styleClipboard);
  styleClipboardRef.current = styleClipboard;
  const [isDraggingElement, setIsDraggingElement] = useState(false);
  const [productAnimTick, setProductAnimTick] = useState(0);
  const [borderEditorAnchor, setBorderEditorAnchor] = useState<{ left: number; top: number } | null>(null);
  const [fieldStyleEditorTab, setFieldStyleEditorTab] = useState<'borda' | 'fonte'>('borda');
  const reduceMotion = useReducedMotion();
  const simulatedViewport = useSimulatedViewport();

  useEffect(() => {
    if (!editingBorderField) {
      setFieldStyleEditorTab('borda');
    }
  }, [editingBorderField]);

  /** Commit imediato: o batch por rAF deixava o pai/desenhos defasados e o resize parecia “travado”. */
  const commitPlacements = useCallback(
    (next: PanfletoElementPlacements) => {
      elementPlacementsRef.current = next;
      onElementPlacementsChange?.(next);
    },
    [onElementPlacementsChange]
  );

  /** Sem produto na tela: não mostrar blocos alimentados pelo cadastro (preview/editor ainda podem passar promo fictício). */
  const promoForTexts = useMemo((): Promotion => {
    if (panfletoHasProductsOnScreen === false) {
      return {
        id: 0,
        name: '',
        price: 0,
        unit: '',
        category: '',
        validUntil: '',
        createdAt: promo.createdAt,
      };
    }
    return promo;
  }, [panfletoHasProductsOnScreen, promo]);

  const t = resolvePanfletoDisplayTexts(layoutStyle?.panfletoContent, promoForTexts);
  const fieldStyles = useMemo(
    () => layoutStyle?.panfletoContent?.styles ?? {},
    [layoutStyle?.panfletoContent?.styles]
  );
  const precoDeSt = fieldStyles.precoDe;
  const precoPorSt = fieldStyles.precoPor;
  const precoDeSplitOn = isPanfletoPriceSplitOn(precoDeSt);
  const precoPorSplitOn = isPanfletoPriceSplitOn(precoPorSt);
  const precoDeCaptionLayout: PanfletoPriceCaptionLayout =
    precoDeSt?.priceCaptionLayout === 'left' ? 'left' : 'above';
  const precoPorCaptionLayout: PanfletoPriceCaptionLayout =
    precoPorSt?.priceCaptionLayout === 'left' ? 'left' : 'above';
  const vBase = getPanfletoFieldBase(layoutStyle, 'validade');
  const fmtPrecoTxt = (s: string) => {
    const x = s.trim();
    if (!x) return '';
    return /r\$/i.test(x) ? x : `R$ ${x}`;
  };
  const precoDeLabel = t.precoDe ? fmtPrecoTxt(t.precoDe) : '';
  const precoPorLabel = t.precoPor ? fmtPrecoTxt(t.precoPor) : '';
  const priceTypo = getTextStyle('price');
  /** Mesma base tipográfica do "Por" (layoutStyle.price); overrides em styles.precoDe / priceValue*. */
  const precoDeValueBaseStyle: React.CSSProperties = {
    ...priceTypo,
    textDecoration: 'line-through',
  };
  const hideLinha1 = panfletoFieldIsHidden(layoutStyle?.panfletoContent, 'linha1');
  const hideLinha2 = panfletoFieldIsHidden(layoutStyle?.panfletoContent, 'linha2');
  const hidePrecoDe = panfletoFieldIsHidden(layoutStyle?.panfletoContent, 'precoDe');
  const hidePrecoPor = panfletoFieldIsHidden(layoutStyle?.panfletoContent, 'precoPor');
  const hideQuantidade = panfletoFieldIsHidden(layoutStyle?.panfletoContent, 'quantidade');
  const hideValidade = panfletoFieldIsHidden(layoutStyle?.panfletoContent, 'validade');
  const productNameAlign = resolvePanfletoProductNameAlign(layoutStyle?.panfletoContent);
  const getFieldTypographyStyle = useCallback(
    (field: PanfletoStyleFieldKey, base: React.CSSProperties): React.CSSProperties => {
      const ov = layoutStyle?.panfletoContent?.styles?.[field];
      const out: React.CSSProperties = { ...base };
      if (ov?.color) out.color = ov.color;
      if (ov?.fontSize !== undefined && ov.fontSize >= 0) {
        out.fontSize = ov.fontSize === 0 ? '1px' : `${ov.fontSize}px`;
      }
      const shadowCss = buildPanfletoTextOuterShadowCss(ov?.textOuterShadow);
      if (shadowCss) out.textShadow = shadowCss;
      return out;
    },
    [layoutStyle?.panfletoContent?.styles]
  );
  const getFieldBorderGroupStyle = useCallback(
    (field: PanfletoStyleFieldKey): React.CSSProperties | undefined => {
      const border = layoutStyle?.panfletoContent?.styles?.[field]?.border;
      if (!border?.enabled) return undefined;
      const fx = buildPanfletoTextFieldBorderEffectStyle(border);
      return {
        ...fx,
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 'fit-content',
        maxWidth: '100%',
        overflow: 'visible',
      };
    },
    [layoutStyle?.panfletoContent?.styles]
  );
  /** Tarjas de preço — mesma regra do POR: animação (`transform`) não pode ficar no mesmo nó do fundo/padding-box. */
  const precoDeShellStyle = getFieldBorderGroupStyle('precoDe');
  const precoPorShellStyle = getFieldBorderGroupStyle('precoPor');
  const hasProductPhoto = Boolean(panfletoProdutoFotoViaApi && panfletoFotoObjectUrl);
  const hasRealProduct =
    promo.id > 0 ||
    Boolean((promo.name || '').trim()) ||
    Boolean((promo.category || '').trim()) ||
    Number(promo.price || 0) > 0 ||
    hasProductPhoto;
  const showcaseCfg = useMemo(() => {
    const raw = layoutStyle?.panfletoContent?.productShowcase ?? {};
    const DEFAULT_TEXT_PANEL = '#ef4444';
    const DEFAULT_PRODUCT_PANEL = '#f8fafc';
    // Fundo geral (`backgroundColor`) e cores da borda compartilhada são independentes.
    const textPanelBgColor =
      String(raw.textPanelBgColor ?? DEFAULT_TEXT_PANEL).trim() || DEFAULT_TEXT_PANEL;
    const productPanelBgColor =
      String(raw.productPanelBgColor ?? DEFAULT_PRODUCT_PANEL).trim() || DEFAULT_PRODUCT_PANEL;
    const scaleRaw = Number(raw.sharedFrameScale ?? 1);
    const sharedFrameScale = Number.isFinite(scaleRaw) ? Math.min(1, Math.max(0.35, scaleRaw)) : 1;
    return {
      sharedFrameScale,
      panelGapPx: Math.max(0, Number(raw.panelGapPx ?? 0)),
      sharedBorderWidth: Math.max(0, Number(raw.sharedBorderWidth ?? 2)),
      sharedBorderRadius: Math.max(0, Number(raw.sharedBorderRadius ?? 26)),
      sharedBorderColor: raw.sharedBorderColor || '#f1f5f9',
      textPanelBgColor,
      productPanelBgColor,
      textPanelPaddingPx: 0,
      productPanelPaddingPx: 0,
    };
  }, [layoutStyle?.panfletoContent?.productShowcase]);
  /** Borda compartilhada / split: tela com produto + dados de produto no `promo` (slide). */
  const useShowcaseLayout =
    panfletoHasProductsOnScreen === false ? false : hasRealProduct;
  /** Display com vitrine/layout livre: textos/preços animam com Framer Motion; moldura + foto permanecem estáveis. */
  const useFramerFreeformProductSwap =
    !reduceMotion &&
    density === 'display' &&
    !positionEditMode &&
    panfletoHasProductsOnScreen !== false &&
    hasRealProduct &&
    (useShowcaseLayout || useFreeformLayout);
  const shouldAnimateProductElements =
    density === 'display' && productAnimTick > 0 && !positionEditMode && !useFramerFreeformProductSwap;
  /** No showcase: slide em X. Com borda compartilhada, translateX cruza o corte vertical e gera “borda fantasma” na troca de produto. */
  const productAnimClass = shouldAnimateProductElements
    ? useShowcaseLayout
      ? 'panfleto-product-swap-in-showcase'
      : 'panfleto-product-swap-in'
    : '';
  const useShowcasePositionMode = positionEditMode && useShowcaseLayout;
  /** Escala visual da vitrine inteira (borda + conteúdo). `scale(s)` sobre 100%×100% do palco — s<1 deixa margem ao redor (fundo do panfleto). */
  const showcaseFrameScale = showcaseCfg.sharedFrameScale;

  /** Limites em % do palco da vitrine. `layoutW`/`layoutH` vêm de offsetWidth/offsetHeight — não encolhem com `scale(s)`; a escala só afeta o desenho na tela. */
  const getShowcaseBoundsForElement = useCallback(
    (id: PanfletoDraggableElementId, layoutW: number, layoutH: number) => {
      const LW = Math.max(1, layoutW);
      const LH = Math.max(1, layoutH);
      const borderPctX = (showcaseCfg.sharedBorderWidth / LW) * 100;
      const borderPctY = (showcaseCfg.sharedBorderWidth / LH) * 100;
      const textPaddingPctX = (showcaseCfg.textPanelPaddingPx / LW) * 100;
      const textPaddingPctY = (showcaseCfg.textPanelPaddingPx / LH) * 100;
      const productPaddingPctX = (showcaseCfg.productPanelPaddingPx / LW) * 100;
      const productPaddingPctY = (showcaseCfg.productPanelPaddingPx / LH) * 100;
      const seamHalfBorderPct = borderPctX / 2;

      if (isProductPanelElement(id)) {
        return {
          minXPct: 50 + seamHalfBorderPct + productPaddingPctX,
          maxXPct: 100 - borderPctX - productPaddingPctX,
          minYPct: borderPctY + productPaddingPctY,
          maxYPct: 100 - borderPctY - productPaddingPctY,
        };
      }
      return {
        minXPct: borderPctX + textPaddingPctX,
        maxXPct: 50 - seamHalfBorderPct - textPaddingPctX,
        minYPct: borderPctY + textPaddingPctY,
        maxYPct: 100 - borderPctY - textPaddingPctY,
      };
    },
    [showcaseCfg]
  );

  const clampToShowcasePanel = useCallback(
    (
      id: PanfletoDraggableElementId,
      xPct: number,
      yPct: number,
      layoutW: number,
      layoutH: number,
      elementWidthPx?: number,
      elementHeightPx?: number
    ) => {
      const bounds = getShowcaseBoundsForElement(id, layoutW, layoutH);
      let effectiveWidthPx = elementWidthPx ?? 0;
      let effectiveHeightPx = elementHeightPx ?? 0;
      // Em escala menor da vitrine, considerar largura/altura dos textos no clamp pode gerar
      // minX > maxX (ou minY > maxY) e "prender" o elemento num ponto fixo ao arrastar.
      // Para textos, o clamp deve ser pelo ponto âncora; restrição de tamanho só na imagem.
      if (id !== 'imagemProduto') {
        effectiveWidthPx = 0;
        effectiveHeightPx = 0;
      }
      const LW = Math.max(1, layoutW);
      const LH = Math.max(1, layoutH);
      const halfWidthPct = (effectiveWidthPx / 2 / LW) * 100;
      const halfHeightPct = (effectiveHeightPx / 2 / LH) * 100;
      let minX = bounds.minXPct + halfWidthPct;
      let maxX = bounds.maxXPct - halfWidthPct;
      let minY = bounds.minYPct + halfHeightPct;
      let maxY = bounds.maxYPct - halfHeightPct;
      if (minX > maxX) {
        const mid = (bounds.minXPct + bounds.maxXPct) / 2;
        minX = mid;
        maxX = mid;
      }
      if (minY > maxY) {
        const mid = (bounds.minYPct + bounds.maxYPct) / 2;
        minY = mid;
        maxY = mid;
      }
      return {
        xPct: Math.max(minX, Math.min(maxX, xPct)),
        yPct: Math.max(minY, Math.min(maxY, yPct)),
      };
    },
    [getShowcaseBoundsForElement]
  );

  useEffect(() => {
    if (density !== 'display') {
      prevPromoIdRef.current = promo.id;
      return;
    }
    const prevId = prevPromoIdRef.current;
    if (prevId !== null && prevId !== promo.id) {
      setProductAnimTick((k) => k + 1);
    }
    prevPromoIdRef.current = promo.id;
  }, [promo.id, density]);

  const patchPlacement = useCallback(
    (id: PanfletoDraggableElementId, patch: Partial<{ xPct: number; yPct: number; widthPx: number; heightPx: number }>) => {
      const placements = elementPlacementsRef.current;
      const prev = placements[id] ?? getShowcaseDefaultPlacement(id);
      const next: PanfletoElementPlacements = {
        ...placements,
        [id]: {
          ...prev,
          ...patch,
        },
      };
      if (id === 'imagemProduto') {
        const w = next.imagemProduto?.widthPx;
        const h = next.imagemProduto?.heightPx;
        if (w !== undefined) next.imagemProduto!.widthPx = Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, w);
        if (h !== undefined) next.imagemProduto!.heightPx = Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, h);
      }
      commitPlacements(next);
    },
    [commitPlacements]
  );

  useEffect(() => {
    if (!positionEditMode) {
      setEditingBorderField(null);
      setBorderEditorAnchor(null);
      setStyleClipboard(null);
    }
  }, [positionEditMode]);

  const clearStyleClipboard = useCallback(() => {
    setStyleClipboard(null);
  }, []);

  const beginCopyFieldStyle = useCallback(
    (field: PanfletoStyleFieldKey) => {
      if (!onFieldStyleChange) return;
      const snap = snapshotPanfletoFieldVisualStyle(layoutStyle, field, fieldStyles[field]);
      setStyleClipboard({ sourceField: field, style: snap });
      setEditingBorderField(null);
      setBorderEditorAnchor(null);
    },
    [fieldStyles, layoutStyle, onFieldStyleChange]
  );

  const pasteStyleClipboardOnto = useCallback(
    (targetField: PanfletoStyleFieldKey) => {
      const clip = styleClipboardRef.current;
      if (!clip || !onFieldStyleChange) return false;
      if (targetField === clip.sourceField) {
        clearStyleClipboard();
        return true;
      }
      const adapted = adaptPanfletoFieldVisualStyleForTarget(clip.style, targetField);
      onFieldStyleChange(targetField, adapted, { replace: true });
      clearStyleClipboard();
      return true;
    },
    [clearStyleClipboard, onFieldStyleChange]
  );

  // Modo "copiar estilo": Escape ou clique fora de um campo de texto cancela a cópia.
  useEffect(() => {
    if (!styleClipboard || !positionEditMode) return;
    const doc = getOwnerDocument(canvasRef.current);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        clearStyleClipboard();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-panfleto-style-clipboard-ui="true"]')) return;
      if (target.closest('[data-panfleto-free-id]')) return;
      if (target.closest('[data-panfleto-border-pencil="true"]')) return;
      if (target.closest('[data-panfleto-copy-style="true"]')) return;
      clearStyleClipboard();
    };
    doc.addEventListener('keydown', onKeyDown, true);
    doc.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      doc.removeEventListener('keydown', onKeyDown, true);
      doc.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [styleClipboard, positionEditMode, clearStyleClipboard]);

  // IMPORTANTE: não "reclamp" automático ao entrar no modo de edição.
  // O reclamp de todos os elementos na ativação pode colapsar posições em escalas menores
  // e causar sensação de tela "travada". O clamp continua ativo durante o arraste (pointermove).

  useEffect(() => {
    if (!editingBorderField) return;
    const onWindowPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const clickedInsideEditor = target.closest('[data-panfleto-border-editor="true"]');
      const clickedOnPencil = target.closest('[data-panfleto-border-pencil="true"]');
      if (clickedInsideEditor || clickedOnPencil) {
        return;
      }
      setEditingBorderField(null);
      setBorderEditorAnchor(null);
    };
    const doc = getOwnerDocument(canvasRef.current);
    doc.addEventListener('pointerdown', onWindowPointerDown, true);
    return () => {
      doc.removeEventListener('pointerdown', onWindowPointerDown, true);
    };
  }, [editingBorderField, simulatedViewport.active, simulatedViewport.width, simulatedViewport.height]);

  useEffect(() => {
    const onWinPointerMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId || !canvasRef.current) return;
      const el = canvasRef.current;
      // offsetWidth/Height refletem o layout “lógico” (pré-scale); rect pode ser 0 em alguns frames.
      if (el.offsetWidth <= 0 || el.offsetHeight <= 0) return;
      const rect = el.getBoundingClientRect();

      if (d.kind === 'move') {
        const travel = Math.hypot(e.clientX - d.startClientX, e.clientY - d.startClientY);
        if (!d.hasMoved && travel < DRAG_MOVE_THRESHOLD_PX) {
          return;
        }
        d.hasMoved = true;

        let pointerXPct: number;
        let pointerYPct: number;
        if (useShowcasePositionMode) {
          const layW = Math.max(1, el.offsetWidth);
          const layH = Math.max(1, el.offsetHeight);
          const tight = el.getBoundingClientRect();
          const cx = tight.left + tight.width / 2;
          const cy = tight.top + tight.height / 2;
          const layoutLeft = cx - layW / 2;
          const layoutTop = cy - layH / 2;
          const s = Math.max(0.001, showcaseFrameScale);
          const textPane = showcaseTextPaneRef.current?.getBoundingClientRect();
          const productPane = showcaseProductPaneRef.current?.getBoundingClientRect();
          const pane = isProductPanelElement(d.id) ? productPane : textPane;
          let sx = e.clientX;
          let sy = e.clientY;
          if (pane && pane.width > 0 && pane.height > 0) {
            if (d.id === 'imagemProduto') {
              const currentEl = el.querySelector<HTMLElement>(`[data-panfleto-free-id="${d.id}"]`);
              const cr = currentEl?.getBoundingClientRect();
              const halfW = (cr?.width ?? 0) / 2;
              const halfH = (cr?.height ?? 0) / 2;
              sx = Math.max(pane.left + halfW, Math.min(pane.right - halfW, sx));
              sy = Math.max(pane.top + halfH, Math.min(pane.bottom - halfH, sy));
            } else {
              sx = Math.max(pane.left, Math.min(pane.right, sx));
              sy = Math.max(pane.top, Math.min(pane.bottom, sy));
            }
            // Inverter scale(s) com origem no centro: o clamp é em tela (pane já escalado); % são do layout cheio.
            const layoutCx = layoutLeft + layW / 2;
            const layoutCy = layoutTop + layH / 2;
            const xLayoutPx = layW / 2 + (sx - layoutCx) / s;
            const yLayoutPx = layH / 2 + (sy - layoutCy) / s;
            pointerXPct = clampPct((xLayoutPx / layW) * 100);
            pointerYPct = clampPct((yLayoutPx / layH) * 100);
          } else {
            const layoutCx = layoutLeft + layW / 2;
            const layoutCy = layoutTop + layH / 2;
            const xLayoutPx = layW / 2 + (e.clientX - layoutCx) / s;
            const yLayoutPx = layH / 2 + (e.clientY - layoutCy) / s;
            pointerXPct = clampPct((xLayoutPx / layW) * 100);
            pointerYPct = clampPct((yLayoutPx / layH) * 100);
          }
        } else {
          pointerXPct = clampPct(((e.clientX - rect.left) / Math.max(1, rect.width)) * 100);
          pointerYPct = clampPct(((e.clientY - rect.top) / Math.max(1, rect.height)) * 100);
        }

        // Mantém o ponto do clique relativo à âncora — não cola a âncora no cursor.
        let xPct = clampPct(pointerXPct - d.grabOffsetXPct);
        let yPct = clampPct(pointerYPct - d.grabOffsetYPct);

        if (useShowcasePositionMode) {
          const layW = Math.max(1, el.offsetWidth);
          const layH = Math.max(1, el.offsetHeight);
          const s = Math.max(0.001, showcaseFrameScale);
          const currentEl = el.querySelector<HTMLElement>(`[data-panfleto-free-id="${d.id}"]`);
          const cr = currentEl?.getBoundingClientRect();
          let layoutImgW = 0;
          let layoutImgH = 0;
          if (d.id === 'imagemProduto') {
            if (cr && cr.width > 0) {
              layoutImgW = cr.width / s;
              layoutImgH = cr.height / s;
            } else {
              layoutImgW = d.startPlacement.widthPx ?? 0;
              layoutImgH = d.startPlacement.heightPx ?? 0;
            }
          }
          const clamped = clampToShowcasePanel(d.id, xPct, yPct, layW, layH, layoutImgW, layoutImgH);
          xPct = clamped.xPct;
          yPct = clamped.yPct;
        }

        patchPlacement(d.id, { xPct, yPct });
      } else if (d.kind === 'resize-image') {
        const dx = e.clientX - d.startClientX;
        const dy = e.clientY - d.startClientY;
        const w0 = d.startPlacement.widthPx ?? PANFLETO_IMAGEM_MIN_WIDTH_PX;
        const h0 = d.startPlacement.heightPx ?? PANFLETO_IMAGEM_MIN_HEIGHT_PX;
        // Como a imagem é ancorada pelo centro (translate -50%/-50%),
        // o canto inferior-direito se move metade da variação de largura/altura.
        // Multiplicar por 2 mantém o handle acompanhando o cursor (feeling 1:1).
        const s = useShowcasePositionMode ? Math.max(0.001, showcaseFrameScale) : 1;
        const axisX = d.resizeAxis?.x ?? 1;
        const axisY = d.resizeAxis?.y ?? 1;
        const widthDelta = axisX === 0 ? 0 : (dx * axisX * 2) / s;
        const heightDelta = axisY === 0 ? 0 : (dy * axisY * 2) / s;
        // `widthPx`/`heightPx` são do espaço de layout (pré-scale); o cursor está em px de tela (pós-scale).
        let nextWidth = Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, w0 + widthDelta);
        let nextHeight = Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, h0 + heightDelta);
        if (useShowcasePositionMode) {
          const layW = Math.max(1, el.offsetWidth);
          const layH = Math.max(1, el.offsetHeight);
          const bounds = getShowcaseBoundsForElement('imagemProduto', layW, layH);
          const maxWidthRaw = ((bounds.maxXPct - bounds.minXPct) / 100) * layW;
          const maxHeightRaw = ((bounds.maxYPct - bounds.minYPct) / 100) * layH;
          // No showcase, permitir largura maior que a coluna da foto para não "travar" cedo.
          // Mantemos um teto amplo (largura total do palco) só para evitar valores absurdos.
          const maxWidthSoft = layW;
          if (maxWidthSoft > PANFLETO_IMAGEM_MIN_WIDTH_PX + 1) {
            nextWidth = Math.min(maxWidthSoft, nextWidth);
          }
          // Quando o painel útil fica menor que o mínimo, não travar em um valor fixo.
          if (maxHeightRaw > PANFLETO_IMAGEM_MIN_HEIGHT_PX + 1) {
            nextHeight = Math.min(maxHeightRaw, nextHeight);
          }
        }
        patchPlacement('imagemProduto', {
          widthPx: nextWidth,
          heightPx: nextHeight,
        });
      }
    };

    const onWinPointerUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      dragRef.current = null;
      setIsDraggingElement(false);
    };

    // Documento do canvas (iframe da simulação ou a página). O window pai não recebe pointer do iframe.
    const doc = getOwnerDocument(canvasRef.current);
    const win = getOwnerWindow(canvasRef.current);
    doc.addEventListener('pointermove', onWinPointerMove, true);
    doc.addEventListener('pointerup', onWinPointerUp, true);
    doc.addEventListener('pointercancel', onWinPointerUp, true);
    win.addEventListener('pointermove', onWinPointerMove, true);
    win.addEventListener('pointerup', onWinPointerUp, true);
    win.addEventListener('pointercancel', onWinPointerUp, true);
    return () => {
      doc.removeEventListener('pointermove', onWinPointerMove, true);
      doc.removeEventListener('pointerup', onWinPointerUp, true);
      doc.removeEventListener('pointercancel', onWinPointerUp, true);
      win.removeEventListener('pointermove', onWinPointerMove, true);
      win.removeEventListener('pointerup', onWinPointerUp, true);
      win.removeEventListener('pointercancel', onWinPointerUp, true);
    };
  }, [
    patchPlacement,
    useShowcasePositionMode,
    showcaseFrameScale,
    getShowcaseBoundsForElement,
    clampToShowcasePanel,
    simulatedViewport.active,
    simulatedViewport.width,
    simulatedViewport.height,
  ]);

  const beginDragMove = (id: PanfletoDraggableElementId) => (e: React.PointerEvent) => {
    if (!positionEditMode || !onElementPlacementsChange) return;
    if (editingBorderField) return;

    // Colar estilo: clique no container do texto de destino (não inicia arraste).
    const clip = styleClipboardRef.current;
    if (clip) {
      const mapped = FIELD_BY_ELEMENT_ID[id];
      e.preventDefault();
      e.stopPropagation();
      if (mapped) {
        pasteStyleClipboardOnto(mapped);
      } else {
        clearStyleClipboard();
      }
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const cur = elementPlacementsRef.current[id] ?? getShowcaseDefaultPlacement(id);
    const targetRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const canvas = canvasRef.current;
    const s = useShowcaseLayout ? Math.max(0.001, showcaseFrameScale) : 1;
    const layoutW = id === 'imagemProduto' ? targetRect.width / s : targetRect.width;
    const layoutH = id === 'imagemProduto' ? targetRect.height / s : targetRect.height;

    // Offset clique → âncora atual (em %). Sem isso o 1º move cola a âncora no mouse.
    let grabOffsetXPct = 0;
    let grabOffsetYPct = 0;
    if (canvas && canvas.offsetWidth > 0 && canvas.offsetHeight > 0) {
      const canvasRect = canvas.getBoundingClientRect();
      if (useShowcaseLayout) {
        const layW = Math.max(1, canvas.offsetWidth);
        const layH = Math.max(1, canvas.offsetHeight);
        const cx = canvasRect.left + canvasRect.width / 2;
        const cy = canvasRect.top + canvasRect.height / 2;
        const xLayoutPx = layW / 2 + (e.clientX - cx) / s;
        const yLayoutPx = layH / 2 + (e.clientY - cy) / s;
        const pointerXPct = clampPct((xLayoutPx / layW) * 100);
        const pointerYPct = clampPct((yLayoutPx / layH) * 100);
        grabOffsetXPct = pointerXPct - cur.xPct;
        grabOffsetYPct = pointerYPct - cur.yPct;
      } else if (canvasRect.width > 0 && canvasRect.height > 0) {
        const pointerXPct = clampPct(((e.clientX - canvasRect.left) / canvasRect.width) * 100);
        const pointerYPct = clampPct(((e.clientY - canvasRect.top) / canvasRect.height) * 100);
        grabOffsetXPct = pointerXPct - cur.xPct;
        grabOffsetYPct = pointerYPct - cur.yPct;
      }
    }

    dragRef.current = {
      kind: 'move',
      id,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      grabOffsetXPct,
      grabOffsetYPct,
      hasMoved: false,
      startPlacement: {
        ...cur,
        widthPx: layoutW,
        heightPx: layoutH,
      },
    };
    setIsDraggingElement(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const beginResizeImage = (axis: { x: -1 | 0 | 1; y: -1 | 0 | 1 }) => (e: React.PointerEvent) => {
    if (!positionEditMode || !onElementPlacementsChange) return;
    if (editingBorderField) return;
    e.preventDefault();
    e.stopPropagation();
    const cur = elementPlacementsRef.current.imagemProduto ?? {
      xPct: 50,
      yPct: 50,
      widthPx: 380,
      heightPx: 380,
    };
    // Baseline = tamanho real desenhado (pós-scale na vitrine), não só JSON.
    const shell = (e.currentTarget as HTMLElement).parentElement;
    const s = useShowcaseLayout ? Math.max(0.001, showcaseFrameScale) : 1;
    const vr = shell?.getBoundingClientRect();
    let w0 = cur.widthPx ?? 380;
    let h0 = cur.heightPx ?? 380;
    if (vr && vr.width > 0 && vr.height > 0) {
      w0 = vr.width / s;
      h0 = vr.height / s;
    }
    dragRef.current = {
      kind: 'resize-image',
      id: 'imagemProduto',
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      grabOffsetXPct: 0,
      grabOffsetYPct: 0,
      hasMoved: false,
      startPlacement: {
        xPct: cur.xPct,
        yPct: cur.yPct,
        widthPx: Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, w0),
        heightPx: Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, h0),
      },
      resizeAxis: axis,
    };
    setIsDraggingElement(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const fitImageToVisibleArea = useCallback(() => {
    if (!onElementPlacementsChange || !canvasRef.current) return;
    const layW = Math.max(1, canvasRef.current.offsetWidth);
    const layH = Math.max(1, canvasRef.current.offsetHeight);
    let widthPx = Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, layW * 0.55);
    let heightPx = Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, layH * 0.7);
    let xPct = 50;
    let yPct = 50;
    if (useShowcasePositionMode) {
      const bounds = getShowcaseBoundsForElement('imagemProduto', layW, layH);
      const panelW = Math.max(1, ((bounds.maxXPct - bounds.minXPct) / 100) * layW);
      const panelH = Math.max(1, ((bounds.maxYPct - bounds.minYPct) / 100) * layH);
      widthPx = Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, panelW * 0.92);
      heightPx = Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, panelH * 0.92);
      xPct = (bounds.minXPct + bounds.maxXPct) / 2;
      yPct = (bounds.minYPct + bounds.maxYPct) / 2;
    }
    patchPlacement('imagemProduto', { xPct, yPct, widthPx, heightPx });
  }, [onElementPlacementsChange, useShowcasePositionMode, getShowcaseBoundsForElement, patchPlacement]);

  const resetImagePlacement = useCallback(() => {
    patchPlacement('imagemProduto', {
      xPct: useShowcasePositionMode ? 75 : 50,
      yPct: 50,
      widthPx: useShowcasePositionMode ? 420 : 380,
      heightPx: useShowcasePositionMode ? 420 : 380,
    });
  }, [patchPlacement, useShowcasePositionMode]);

  const slotStyle = (id: PanfletoDraggableElementId): React.CSSProperties => {
    const p = elementPlacements[id] ?? getShowcaseDefaultPlacement(id);
    const mappedField = FIELD_BY_ELEMENT_ID[id];
    const isEditingThisElement = mappedField ? editingBorderField === mappedField : false;
    const isImage = id === 'imagemProduto';
    const isProductName = id === 'linha1';
    const showcaseTextRightPadPx = Math.max(10, showcaseCfg.sharedBorderWidth + 12);
    const remainingNamePct = useShowcaseLayout
      ? Math.max(8, 50 - p.xPct)
      : Math.max(8, 100 - p.xPct);
    const productNameBoxWidth = useShowcaseLayout
      ? `min(${remainingNamePct}%, calc(50% - ${showcaseTextRightPadPx}px))`
      : `min(96%, ${remainingNamePct}%)`;
    // Nome do produto: faixa até a borda do painel de texto (xPct = esquerda da faixa)
    // para left/center/right corresponderem entre produtos. Demais campos pelo centro.
    //
    // Vertical do nome: a âncora yPct é o centro da 1ª linha (layout montado com 1 linha).
    // Usar height:1lh + overflow:visible + translateY(-50%) faz o -50% ser sempre meia
    // linha — não meia altura do bloco. Com 2+ linhas o texto só cresce para baixo.
    const productNameTypo = isProductName
      ? mergePanfletoFieldStyle(getTextStyle('productName'), 'linha1', layoutStyle?.panfletoContent)
      : undefined;
    const base: React.CSSProperties = {
      position: 'absolute',
      left: `${p.xPct}%`,
      top: `${p.yPct}%`,
      transform: isProductName ? 'translate(0, -50%)' : 'translate(-50%, -50%)',
      display: isImage || isProductName ? 'block' : 'inline-flex',
      width: isImage ? undefined : isProductName ? productNameBoxWidth : 'fit-content',
      maxWidth:
        isImage
          ? undefined
          : useShowcaseLayout
            ? `min(96%, calc(50% - ${Math.max(10, showcaseCfg.sharedBorderWidth + 12)}px))`
            : '96%',
      zIndex: isEditingThisElement ? 120 : positionEditMode ? 20 : 10,
      touchAction: positionEditMode ? 'none' : undefined,
      ...(isProductName
        ? {
            height: '1lh',
            overflow: 'visible',
            // lh do slot = tipografia do nome (senão herda 16px do container e a âncora erra)
            fontSize: productNameTypo?.fontSize,
            lineHeight: productNameTypo?.lineHeight ?? 1.2,
            fontFamily: productNameTypo?.fontFamily,
            fontWeight: productNameTypo?.fontWeight,
          }
        : null),
    };
    if (isImage) {
      const w = Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, p.widthPx ?? 380);
      const h = Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, p.heightPx ?? 380);
      return {
        ...base,
        width: w,
        height: h,
      };
    }
    return base;
  };

  const editOutline =
    positionEditMode && onElementPlacementsChange
      ? styleClipboard
        ? 'ring-2 ring-blue-500/70 ring-inset cursor-copy'
        : 'ring-1 ring-blue-500/40 ring-inset cursor-grab active:cursor-grabbing'
      : '';

  const canEditFieldBorder = positionEditMode && !!onFieldStyleChange;

  const renderProductNameAlignButtons = (
    selected: ReturnType<typeof resolvePanfletoProductNameAlign>,
    extraClass = ''
  ) => {
    if (!onFieldStyleChange) return null;
    return (
      <div className={`flex overflow-hidden rounded-md border border-gray-300 ${extraClass}`} role="group" aria-label="Alinhamento do nome do produto">
        {(
          [
            { id: 'left', label: 'Esquerda' },
            { id: 'center', label: 'Centro' },
            { id: 'right', label: 'Direita' },
          ] as const
        ).map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`flex-1 px-2 py-1.5 text-xs font-medium ${
              selected === opt.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onFieldStyleChange('linha1', { textAlign: opt.id });
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  };

  const updateBorder = useCallback(
    (field: PanfletoStyleFieldKey, patch: Partial<NonNullable<PanfletoFieldStyle['border']>>) => {
      if (!onFieldStyleChange) return;
      const prev = fieldStyles[field];
      const currentBorder = prev?.border ?? {};
      onFieldStyleChange(field, {
        border: {
          ...currentBorder,
          ...patch,
        },
      });
    },
    [fieldStyles, onFieldStyleChange]
  );

  const updateTextOuterShadow = useCallback(
    (field: PanfletoStyleFieldKey, patch: Partial<NonNullable<PanfletoFieldStyle['textOuterShadow']>>) => {
      if (!onFieldStyleChange) return;
      const prev = fieldStyles[field];
      const cur = prev?.textOuterShadow ?? {};
      onFieldStyleChange(field, {
        textOuterShadow: {
          ...cur,
          ...patch,
        },
      });
    },
    [fieldStyles, onFieldStyleChange]
  );

  const renderBorderEditor = (field: PanfletoStyleFieldKey) => {
    if (isDraggingElement) return null;
    if (!canEditFieldBorder || editingBorderField !== field) return null;
    if (!borderEditorAnchor) return null;
    const portalDoc = getOwnerDocument(canvasRef.current);
    if (!portalDoc.body) return null;
    const current = fieldStyles[field]?.border;
    const shadow = fieldStyles[field]?.textOuterShadow;
    const fieldOv = fieldStyles[field];
    const fieldBase = getPanfletoFieldBase(layoutStyle, field);
    const isPricePairField = field === 'precoDe' || field === 'precoPor';
    const priceCaptionLayout: PanfletoPriceCaptionLayout =
      fieldOv?.priceCaptionLayout === 'left' ? 'left' : 'above';
    const priceSplitOn = isPanfletoPriceSplitOn(fieldOv);
    const panelTopPx = Math.round(borderEditorAnchor.top);
    const viewH = getOwnerWindow(canvasRef.current).innerHeight;
    const panelMaxHeight = `min(88dvh, max(11rem, calc(${viewH}px - ${panelTopPx}px - 12px)))`;
    /** Portal: `fixed` dentro de slot com `transform` vira “fixed” ao ancestral e o painel encolhe/corta ao mudar espessura da borda. */
    const panel = (
      <div
        data-panfleto-border-editor="true"
        className="fixed z-[130] flex w-[min(22rem,calc(100vw-16px))] min-w-[18rem] max-w-[min(22rem,calc(100vw-16px))] flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg pointer-events-auto box-border"
        style={{
          left: borderEditorAnchor.left,
          top: borderEditorAnchor.top,
          transform: 'translate(-50%, 0)',
          maxHeight: panelMaxHeight,
        }}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 px-3 pt-3 pb-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-xs font-semibold text-gray-800">Personalizar campo</span>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-blue-600"
                checked={fieldOv?.fontSize === 0}
                onChange={(e) => {
                  if (!onFieldStyleChange) return;
                  onFieldStyleChange(field, { fontSize: e.target.checked ? 0 : 1 });
                }}
              />
              Ocultar campo
            </label>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              data-panfleto-copy-style="true"
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
              title="Copiar estilo visual deste campo e clicar em outro texto para colar"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                beginCopyFieldStyle(field);
              }}
            >
              <Copy className="h-3.5 w-3.5" />
              Copiar estilo
            </button>
            <button
              type="button"
              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEditingBorderField(null);
                setBorderEditorAnchor(null);
              }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="mx-3 mb-2 flex shrink-0 rounded-lg bg-gray-100 p-0.5" role="tablist" aria-label="Seções do personalizador">
          <button
            type="button"
            role="tab"
            aria-selected={fieldStyleEditorTab === 'borda'}
            className={`flex-1 rounded-md px-2 py-2 text-xs font-medium transition ${
              fieldStyleEditorTab === 'borda'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setFieldStyleEditorTab('borda');
            }}
          >
            Borda
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={fieldStyleEditorTab === 'fonte'}
            className={`flex-1 rounded-md px-2 py-2 text-xs font-medium transition ${
              fieldStyleEditorTab === 'fonte'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setFieldStyleEditorTab('fonte');
            }}
          >
            Fonte
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain px-3 pb-3">
        {fieldStyleEditorTab === 'borda' ? (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={current?.enabled === true}
                onChange={(e) => {
                  const on = e.target.checked;
                  updateBorder(field, {
                    enabled: on,
                    ...(on ? { width: Math.max(1, Number(current?.width ?? 0) || 1) } : {}),
                  });
                }}
              />
              Ativar borda interna
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={current?.useGradient === true}
                onChange={(e) => {
                  if (e.target.checked) {
                    const patch: Partial<NonNullable<PanfletoFieldStyle['border']>> = {
                      useGradient: true,
                      gradientStart: current?.gradientStart || '#7c3aed',
                      gradientEnd: current?.gradientEnd || '#fbbf24',
                      gradientAngleDeg: current?.gradientAngleDeg ?? 135,
                    };
                    if (current?.enabled === true) {
                      patch.width = Math.max(1, Number(current?.width ?? 0) || 1);
                    }
                    updateBorder(field, patch);
                  } else {
                    updateBorder(field, { useGradient: false });
                  }
                }}
              />
              Gradiente na borda (texto)
            </label>
            {current?.useGradient === true ? (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600">Cor inicial</span>
                  <input
                    type="color"
                    className="h-7 w-10 rounded border border-gray-300 p-0"
                    value={normalizeHexColorString(String(current?.gradientStart || '')) || '#7c3aed'}
                    onChange={(e) => updateBorder(field, { gradientStart: e.target.value })}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600">Cor final</span>
                  <input
                    type="color"
                    className="h-7 w-10 rounded border border-gray-300 p-0"
                    value={normalizeHexColorString(String(current?.gradientEnd || '')) || '#fbbf24'}
                    onChange={(e) => updateBorder(field, { gradientEnd: e.target.value })}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600">Ângulo (°)</span>
                  <input
                    type="number"
                    min={0}
                    max={360}
                    className="h-7 w-16 rounded border border-gray-300 px-1 text-xs"
                    value={current?.gradientAngleDeg ?? 135}
                    onChange={(e) =>
                      updateBorder(field, {
                        gradientAngleDeg: Math.max(0, Math.min(360, Number(e.target.value) || 0)),
                      })
                    }
                  />
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-600">Cor sólida</span>
                <input
                  type="color"
                  className="h-7 w-10 rounded border border-gray-300 p-0"
                  value={current?.color || '#000000'}
                  onChange={(e) => updateBorder(field, { color: e.target.value })}
                />
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-600">Espessura</span>
              <input
                type="number"
                min={current?.enabled === true ? 1 : 0}
                max={24}
                className="h-7 w-16 rounded border border-gray-300 px-1 text-xs"
                value={current?.enabled === true ? Math.max(1, Number(current?.width ?? 0) || 1) : current?.width ?? 0}
                onChange={(e) => {
                  const minW = current?.enabled === true ? 1 : 0;
                  updateBorder(field, { width: Math.max(minW, Number(e.target.value) || 0) });
                }}
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-600">Arredondar</span>
              <input
                type="number"
                min={0}
                max={40}
                className="h-7 w-16 rounded border border-gray-300 px-1 text-xs"
                value={current?.radius ?? 0}
                onChange={(e) => updateBorder(field, { radius: Math.max(0, Number(e.target.value) || 0) })}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {isPricePairField ? (
              <label className="flex items-start gap-2 text-xs text-gray-700 leading-snug">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-blue-600"
                  checked={priceSplitOn}
                  onChange={(e) => {
                    if (!onFieldStyleChange) return;
                    if (e.target.checked) {
                      const defaultValueFs = Math.max(
                        1,
                        fieldOv?.fontSize !== undefined && fieldOv.fontSize > 0
                          ? fieldOv.fontSize
                          : fieldBase.fontSize
                      );
                      const defaultValueHexRaw =
                        String(fieldOv?.priceValueColor ?? fieldOv?.color ?? fieldBase.color);
                      const defaultLabelHex =
                        normalizeHexColorString(
                          String(fieldOv?.priceLabelColor ?? fieldOv?.color ?? '#111827')
                        ) || '#111827';
                      const defaultValueHex =
                        normalizeHexColorString(defaultValueHexRaw) || defaultValueHexRaw.trim() || '#111827';
                      onFieldStyleChange(field, {
                        priceSplitCustomize: true,
                        priceCaptionLayout: priceCaptionLayout,
                        priceLabelFontSize: fieldOv?.priceLabelFontSize ?? 14,
                        priceValueFontSize: fieldOv?.priceValueFontSize ?? defaultValueFs,
                        priceLabelColor: defaultLabelHex,
                        priceValueColor: defaultValueHex,
                      });
                    } else {
                      onFieldStyleChange(field, { priceSplitCustomize: false });
                    }
                  }}
                />
                Personalizar título e preço separadamente
              </label>
            ) : null}

            {isPricePairField && priceSplitOn ? (
              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/90 p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600 shrink-0">Tamanho título (px)</span>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    className="h-7 w-16 rounded border border-gray-300 px-1 text-xs text-center"
                    value={fieldOv?.priceLabelFontSize ?? 14}
                    onChange={(e) => {
                      if (!onFieldStyleChange) return;
                      const n = parseInt(e.target.value, 10);
                      if (Number.isNaN(n) || n < 1) return;
                      onFieldStyleChange(field, { priceLabelFontSize: Math.min(200, n) });
                    }}
                    aria-label="Tamanho da fonte do título DE ou POR"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600 shrink-0">Tamanho preço (px)</span>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    className="h-7 w-16 rounded border border-gray-300 px-1 text-xs text-center"
                    value={fieldOv?.priceValueFontSize ?? Math.max(1, fieldBase.fontSize)}
                    onChange={(e) => {
                      if (!onFieldStyleChange) return;
                      const n = parseInt(e.target.value, 10);
                      if (Number.isNaN(n) || n < 1) return;
                      onFieldStyleChange(field, { priceValueFontSize: Math.min(200, n) });
                    }}
                    aria-label="Tamanho da fonte do valor do preço"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600 shrink-0">Cor do título</span>
                  <input
                    type="color"
                    className={panfletoFieldCustomizerColorInputClass}
                    value={hexForPanfletoFieldColor(fieldOv?.priceLabelColor, fieldOv?.color ?? '#111827')}
                    onChange={(e) => {
                      if (!onFieldStyleChange) return;
                      const raw = e.target.value;
                      const cn = normalizeHexColorString(raw);
                      onFieldStyleChange(field, { priceLabelColor: cn || raw });
                    }}
                    title="Cor do texto DE ou POR"
                    aria-label="Cor do texto DE ou POR"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600 shrink-0">Cor do preço</span>
                  <input
                    type="color"
                    className={panfletoFieldCustomizerColorInputClass}
                    value={hexForPanfletoFieldColor(
                      fieldOv?.priceValueColor,
                      fieldBase.color
                    )}
                    onChange={(e) => {
                      if (!onFieldStyleChange) return;
                      const raw = e.target.value;
                      const cn = normalizeHexColorString(raw);
                      onFieldStyleChange(field, { priceValueColor: cn || raw });
                    }}
                    title="Cor do valor do preço"
                    aria-label="Cor do valor do preço"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-600">Posição do título</span>
                  <select
                    className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 shadow-sm"
                    value={priceCaptionLayout}
                    onChange={(e) => {
                      if (!onFieldStyleChange) return;
                      const v = e.target.value === 'left' ? 'left' : 'above';
                      onFieldStyleChange(field, { priceCaptionLayout: v });
                    }}
                    aria-label="Posição do título em relação ao preço"
                  >
                    <option value="above">Acima do preço</option>
                    <option value="left">À esquerda do preço</option>
                  </select>
                </div>
              </div>
            ) : null}

            {!(isPricePairField && priceSplitOn) ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-600 shrink-0">Cor da fonte</span>
                  <input
                    type="color"
                    className={panfletoFieldCustomizerColorInputClass}
                    value={hexForPanfletoFieldColor(fieldOv?.color, fieldBase.color)}
                    onChange={(e) => {
                      if (!onFieldStyleChange) return;
                      const raw = e.target.value;
                      const cn = normalizeHexColorString(raw);
                      onFieldStyleChange(field, { color: cn || raw });
                    }}
                    title="Cor da fonte"
                    aria-label="Cor da fonte"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-600 shrink-0">Tamanho (px)</span>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    disabled={fieldOv?.fontSize === 0}
                    className="h-7 w-16 shrink-0 rounded border border-gray-300 px-1 text-xs text-center disabled:bg-gray-100 disabled:text-gray-400"
                    value={panfletoFieldFontSizeForInput(fieldOv, fieldBase)}
                    onChange={(e) => {
                      if (!onFieldStyleChange) return;
                      const n = parseInt(e.target.value, 10);
                      if (Number.isNaN(n) || n < 1) return;
                      onFieldStyleChange(field, { fontSize: Math.min(200, n) });
                    }}
                    onBlur={(e) => {
                      if (!onFieldStyleChange) return;
                      const raw = e.currentTarget.value.trim();
                      if (raw === '' || Number.isNaN(parseInt(raw, 10)) || parseInt(raw, 10) < 1) {
                        onFieldStyleChange(field, { fontSize: 1 });
                      }
                    }}
                    title="Tamanho da fonte em pixels"
                    aria-label="Tamanho da fonte em pixels"
                  />
                </div>
              </>
            ) : null}
            {field === 'linha1' ? (
              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-600">Alinhamento do nome</span>
                {renderProductNameAlignButtons(fieldOv?.textAlign ?? productNameAlign, 'w-full')}
              </div>
            ) : null}
            <div className="border-t border-gray-200 pt-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Sombra externa
              </p>
              <label className="mb-2 flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-blue-600"
                  checked={shadow?.enabled === true}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const d = shadow?.direction;
                      const direction: PanfletoTextOuterShadowDirection =
                        d === 'up' || d === 'down' || d === 'left' || d === 'right' ? d : 'down';
                      updateTextOuterShadow(field, {
                        enabled: true,
                        sizePx: shadow?.sizePx && shadow.sizePx > 0 ? shadow.sizePx : 4,
                        direction,
                      });
                    } else {
                      updateTextOuterShadow(field, { enabled: false });
                    }
                  }}
                />
                Ativar sombra externa
              </label>
              <div className="mb-2 flex flex-col gap-1">
                <span className="text-xs text-gray-600">Direção</span>
                <select
                  className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 shadow-sm disabled:bg-gray-100 disabled:text-gray-400"
                  disabled={shadow?.enabled !== true}
                  value={
                    shadow?.direction === 'up' ||
                    shadow?.direction === 'down' ||
                    shadow?.direction === 'left' ||
                    shadow?.direction === 'right'
                      ? shadow.direction
                      : 'down'
                  }
                  onChange={(e) =>
                    updateTextOuterShadow(field, {
                      direction: e.target.value as PanfletoTextOuterShadowDirection,
                    })
                  }
                  aria-label="Direção da sombra externa"
                >
                  <option value="down">Para baixo</option>
                  <option value="up">Para cima</option>
                  <option value="right">Para a direita</option>
                  <option value="left">Para a esquerda</option>
                </select>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-600">Espessura (px)</span>
                <input
                  type="number"
                  min={1}
                  max={48}
                  disabled={shadow?.enabled !== true}
                  className="h-7 w-16 rounded border border-gray-300 px-1 text-xs disabled:bg-gray-100 disabled:text-gray-400"
                  value={shadow?.sizePx ?? 4}
                  onChange={(e) =>
                    updateTextOuterShadow(field, {
                      sizePx: Math.max(1, Math.min(48, Number(e.target.value) || 4)),
                    })
                  }
                />
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    );
    return createPortal(panel, portalDoc.body);
  };

  const renderFieldPencil = (field: PanfletoStyleFieldKey) => {
    if (isDraggingElement) return null;
    if (!canEditFieldBorder) return null;
    if (styleClipboard) {
      // Em modo colar: não mostra lápis; o clique no container já cola.
      return null;
    }
    if (editingBorderField && editingBorderField !== field) return null;
    return (
      <div className="absolute right-0 bottom-full mb-1.5 z-50 flex items-center gap-1 pointer-events-auto">
        <button
          type="button"
          data-panfleto-copy-style="true"
          className="rounded-full border border-gray-200 bg-white p-1.5 text-blue-600 shadow hover:bg-blue-50"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            beginCopyFieldStyle(field);
          }}
          title="Copiar estilo visual"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          type="button"
          data-panfleto-border-pencil="true"
          className="rounded-full border border-gray-200 bg-white p-1.5 text-gray-600 shadow hover:bg-gray-100"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const prev = editingBorderFieldRef.current;
            if (prev && prev !== field) {
              return;
            }
            if (prev === field) {
              setBorderEditorAnchor(null);
              setEditingBorderField(null);
              return;
            }
            setFieldStyleEditorTab(field === 'linha1' ? 'fonte' : 'borda');
            setBorderEditorAnchor({
              left: rect.left + rect.width / 2,
              top: rect.bottom + 10,
            });
            setEditingBorderField(field);
          }}
          title="Personalizar campo (borda e fonte)"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
    );
  };

  const styleClipboardBanner =
    styleClipboard && positionEditMode
      ? createPortal(
          <div
            data-panfleto-style-clipboard-ui="true"
            className="fixed bottom-4 left-1/2 z-[140] flex max-w-[min(28rem,calc(100vw-1.5rem))] -translate-x-1/2 items-center gap-3 rounded-lg border border-blue-200 bg-white px-4 py-3 text-sm text-gray-800 shadow-lg"
          >
            <Paintbrush className="h-4 w-4 shrink-0 text-blue-600" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-blue-800">Estilo copiado</p>
              <p className="text-xs text-gray-600">
                Clique no texto onde quer aplicar ({PANFLETO_STYLE_FIELD_LABELS[styleClipboard.sourceField]}).
                Clique fora ou Esc para cancelar.
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearStyleClipboard();
              }}
            >
              Cancelar
            </button>
          </div>,
          getOwnerDocument(canvasRef.current).body
        )
      : null;

  useEffect(() => {
    if (!positionEditMode) {
      initFromDomDoneRef.current = false;
      return;
    }
    if (useFreeformLayout || !onInitializeFromCurrentLayout || initFromDomDoneRef.current) {
      return;
    }
    const root = stackMeasureRootRef.current;
    if (!root) return;

    const raf = requestAnimationFrame(() => {
      const rect = root.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      /** Nome do produto: borda esquerda + âncora vertical na 1ª linha. Demais: centro. */
      const readPlacementPct = (
        el: HTMLElement | null,
        anchor: 'center' | 'left',
        yAnchor: 'boxCenter' | 'firstLineCenter' = 'boxCenter'
      ) => {
        if (!el) return null;
        const r = el.getBoundingClientRect();
        const xRef = anchor === 'left' ? r.left : r.left + r.width / 2;
        const xPct = clampPct(((xRef - rect.left) / rect.width) * 100);
        let yRef = r.top + r.height / 2;
        if (yAnchor === 'firstLineCenter') {
          const cs = window.getComputedStyle(el);
          const parsedLh = Number.parseFloat(cs.lineHeight);
          const linePx =
            Number.isFinite(parsedLh) && parsedLh > 0
              ? parsedLh
              : (Number.parseFloat(cs.fontSize) || 16) * 1.2;
          yRef = r.top + linePx / 2;
        }
        const yPct = clampPct(((yRef - rect.top) / rect.height) * 100);
        return { xPct, yPct, widthPx: r.width, heightPx: r.height };
      };

      const getById = (id: PanfletoDraggableElementId) =>
        root.querySelector<HTMLElement>(`[data-panfleto-free-id="${id}"]`);

      const out: PanfletoElementPlacements = {};
      const assign = (id: PanfletoDraggableElementId) => {
        const v = readPlacementPct(
          getById(id),
          id === 'linha1' ? 'left' : 'center',
          id === 'linha1' ? 'firstLineCenter' : 'boxCenter'
        );
        if (!v) return;
        if (id === 'imagemProduto') {
          out[id] = {
            xPct: v.xPct,
            yPct: v.yPct,
            widthPx: Math.max(PANFLETO_IMAGEM_MIN_WIDTH_PX, v.widthPx),
            heightPx: Math.max(PANFLETO_IMAGEM_MIN_HEIGHT_PX, v.heightPx),
          };
          return;
        }
        out[id] = { xPct: v.xPct, yPct: v.yPct };
      };

      assign('texto1');
      assign('texto2');
      assign('texto3');
      assign('linha1');
      assign('linha2');
      assign('imagemProduto');
      assign('precoDe');
      assign('precoPorValor');
      if (out.precoPorValor) {
        // Compatibilidade: manter também a chave legada do label "POR".
        out.precoPorLabel = { ...out.precoPorValor };
      }
      assign('quantidade');
      assign('validade');

      if (Object.keys(out).length > 0) {
        initFromDomDoneRef.current = true;
        onInitializeFromCurrentLayout(out);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [positionEditMode, useFreeformLayout, onInitializeFromCurrentLayout]);

  const stackInner = (
    <div
      ref={stackMeasureRootRef}
      className={
        density === 'display'
          ? `flex flex-col items-center w-full h-full min-h-0 text-center px-4 py-6 sm:py-8 ${
              panfletoProdutoFotoViaApi ? 'justify-start pt-4' : 'justify-center py-8 sm:py-10'
            }`
          : 'flex flex-col items-center justify-center w-full py-10 relative z-10 text-center px-4'
      }
    >
      {t.texto1 ? (
        <p
          data-panfleto-free-id="texto1"
          className={density === 'display' ? 'mb-3 font-medium' : 'mb-3 text-sm font-medium'}
          style={mergePanfletoFieldStyle(
            layoutStyle?.description
              ? { ...getTextStyle('description'), fontWeight: 500 }
              : { color: '#2563eb', fontSize: 14, fontWeight: 500 },
            'texto1',
            layoutStyle?.panfletoContent
          )}
        >
          {t.texto1}
        </p>
      ) : null}

      {t.texto2 ? (
        <p
          data-panfleto-free-id="texto2"
          className={
            density === 'display'
              ? 'text-xl mb-3'
              : 'mb-3'
          }
          style={mergePanfletoFieldStyle(
            layoutStyle?.description ? getTextStyle('description') : { color: '#374151', fontSize: 20 },
            'texto2',
            layoutStyle?.panfletoContent
          )}
        >
          {t.texto2}
        </p>
      ) : null}

      {t.texto3 ? (
        <p
          data-panfleto-free-id="texto3"
          className={density === 'display' ? 'text-xl mb-5' : 'mb-4'}
          style={mergePanfletoFieldStyle(
            layoutStyle?.description ? getTextStyle('description') : { color: '#374151', fontSize: 20 },
            'texto3',
            layoutStyle?.panfletoContent
          )}
        >
          {t.texto3}
        </p>
      ) : density === 'display' ? (
        <div className="mb-4" />
      ) : (
        <div className="mb-4" />
      )}

      {!hideLinha1 && !!(t.linha1 || '').trim() ? (
        <div
          key={`linha1-${productAnimTick}`}
          data-panfleto-free-id="linha1"
          className="self-stretch w-full mb-3"
          style={{ textAlign: productNameAlign }}
        >
          <h3
            className={
              density === 'display'
                ? `inline-block font-bold text-5xl sm:text-6xl max-w-full ${productAnimClass}`
                : 'inline-block mb-0 font-bold max-w-full'
            }
            style={mergePanfletoFieldStyle(getTextStyle('productName'), 'linha1', layoutStyle?.panfletoContent)}
          >
            {t.linha1}
          </h3>
        </div>
      ) : null}

      {t.linha2 && !hideLinha2 ? (
        <p
          key={`linha2-${productAnimTick}`}
          data-panfleto-free-id="linha2"
          className={
            density === 'display'
              ? `text-4xl sm:text-5xl font-extrabold mb-2 leading-tight ${productAnimClass}`
              : 'mb-2 font-extrabold leading-tight'
          }
          style={mergePanfletoFieldStyle(
            layoutStyle?.category ? getTextStyle('category') : { color: '#111827' },
            'linha2',
            layoutStyle?.panfletoContent
          )}
        >
          {t.linha2}
        </p>
      ) : null}

      {panfletoProdutoFotoViaApi && panfletoFotoObjectUrl ? (
        <div key={`foto-${productAnimTick}`} className={`flex w-full shrink-0 items-center justify-center my-1 px-2 ${productAnimClass}`}>
          <div
            data-panfleto-free-id="imagemProduto"
            className="overflow-hidden rounded-lg bg-white/5"
            style={{
              width: 'min(72vw, 800px)',
              height: 'min(50vh, 1500px)',
              maxWidth: '100%',
            }}
          >
            <img
              key={`panfleto-foto-${promo.id}-${panfletoFotoObjectUrl}`}
              src={panfletoFotoObjectUrl}
              alt=""
              className="h-full w-full object-fill object-center"
              decoding="async"
            />
          </div>
        </div>
      ) : null}

      {t.precoDe && !hidePrecoDe ? (
        <div
          key={`preco-de-${productAnimTick}`}
          data-panfleto-free-id="precoDe"
          className="flex flex-col items-center mb-2"
        >
          <div
            style={
              precoDeShellStyle
                ? { ...precoDeShellStyle, lineHeight: 1.25, whiteSpace: 'nowrap', minWidth: 'fit-content' }
                : {
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }
            }
          >
            <div className={pricePairInnerRowClass(precoDeSplitOn, precoDeCaptionLayout, productAnimClass)}>
              <span
                className={
                  precoDeSplitOn
                    ? 'font-medium'
                    : density === 'display'
                      ? 'text-sm font-medium'
                      : 'text-xs font-medium'
                }
                style={mergePanfletoPricePairStyle(
                  { fontFamily: priceTypo.fontFamily, fontSize: '0.875rem', color: '#111827', fontWeight: 600 },
                  'precoDe',
                  'label',
                  layoutStyle?.panfletoContent
                )}
              >
                DE
              </span>
              <span
                className="line-through"
                style={mergePanfletoPricePairStyle(
                  precoDeValueBaseStyle,
                  'precoDe',
                  'value',
                  layoutStyle?.panfletoContent
                )}
              >
                {precoDeLabel}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {t.precoPor && !hidePrecoPor ? (
        <div
          key={`preco-por-${productAnimTick}`}
          data-panfleto-free-id="precoPorValor"
          className={`flex flex-col items-center ${density === 'display' ? 'gap-2 mb-3' : 'gap-1.5 mb-4'}`}
        >
          <div
            style={
              precoPorShellStyle
                ? { ...precoPorShellStyle, lineHeight: 1.25, whiteSpace: 'nowrap', minWidth: 'fit-content' }
                : {
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }
            }
          >
            <div className={pricePairInnerRowClass(precoPorSplitOn, precoPorCaptionLayout, productAnimClass)}>
              <span
                className={
                  precoPorSplitOn
                    ? 'font-medium leading-tight'
                    : density === 'display'
                      ? 'text-sm font-medium leading-tight'
                      : 'text-xs font-medium leading-tight'
                }
                style={mergePanfletoPricePairStyle(
                  { fontFamily: priceTypo.fontFamily, fontSize: '0.875rem', color: '#111827', fontWeight: 600 },
                  'precoPor',
                  'label',
                  layoutStyle?.panfletoContent
                )}
              >
                POR
              </span>
              <span
                className={
                  precoPorSplitOn
                    ? 'font-bold leading-tight'
                    : density === 'display'
                      ? 'text-6xl sm:text-7xl font-bold leading-tight'
                      : 'font-bold leading-tight'
                }
                style={mergePanfletoPricePairStyle(getTextStyle('price'), 'precoPor', 'value', layoutStyle?.panfletoContent)}
              >
                {precoPorLabel || formatBrazilianNumber(promoForTexts.price, layoutStyle?.price?.showCurrency !== false)}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {!hideQuantidade && (!!(t.quantidade || '').trim() || !!(t.unidadeQuantidade || '').trim()) ? (
        <p
          key={`quantidade-${productAnimTick}`}
          data-panfleto-free-id="quantidade"
          className={density === 'display' ? `text-xl mt-1 ${productAnimClass}` : 'text-sm mt-0.5'}
          style={mergePanfletoFieldStyle(getTextStyle('unit'), 'quantidade', layoutStyle?.panfletoContent)}
        >
          {t.quantidade}
          {t.unidadeQuantidade ? ` ${t.unidadeQuantidade}` : ''}
        </p>
      ) : null}

      {t.validadeTexto && !hideValidade ? (
        <p
          data-panfleto-free-id="validade"
          className={
            density === 'display'
              ? 'text-xl font-semibold mt-4 w-full max-w-[min(92vw,980px)] px-2 text-center whitespace-pre-line leading-[1.35] [overflow-wrap:anywhere]'
              : 'text-sm font-semibold mt-3'
          }
          style={mergePanfletoFieldStyle(
            {
              fontFamily: layoutStyle?.validUntil?.fontFamily ?? layoutStyle?.unit?.fontFamily ?? 'Arial, sans-serif',
              color: vBase.color,
              fontSize: `${vBase.fontSize}px`,
              fontWeight: layoutStyle?.validUntil?.fontWeight ?? 600,
            } as React.CSSProperties,
            'validade',
            layoutStyle?.panfletoContent
          )}
        >
          {t.validadeTexto}
        </p>
      ) : null}
    </div>
  );

  const renderFreeformSlots = () => {
    const motionSlideX = !useShowcaseLayout;
    const fotoMotionTransition = { duration: 0.34, ease: [0.22, 0.61, 0.36, 1] as const };
    const fotoMotionInitial = motionSlideX
      ? { opacity: 0, x: 32, scale: 0.95 }
      : { opacity: 0, y: 18, scale: 0.95 };
    const fotoMotionExit = motionSlideX
      ? { opacity: 0, x: -28, scale: 0.95 }
      : { opacity: 0, y: -14, scale: 0.95 };
    const textSlotsTop = (
      <>
      {t.texto1 ? (
        <div
          data-panfleto-free-id="texto1"
          className={`relative text-center items-center justify-center ${editOutline}`}
          style={slotStyle('texto1')}
          onPointerDown={beginDragMove('texto1')}
        >
          {renderFieldPencil('texto1')}
          {renderBorderEditor('texto1')}
          <p
            className="font-medium px-1"
            style={mergePanfletoFieldStyle(
              layoutStyle?.description
                ? { ...getTextStyle('description'), fontWeight: 500 }
                : { color: '#2563eb', fontSize: 14, fontWeight: 500 },
              'texto1',
              layoutStyle?.panfletoContent
            )}
          >
            {t.texto1}
          </p>
        </div>
      ) : null}

      {t.texto2 ? (
        <div
          data-panfleto-free-id="texto2"
          className={`relative text-center items-center justify-center ${editOutline}`}
          style={slotStyle('texto2')}
          onPointerDown={beginDragMove('texto2')}
        >
          {renderFieldPencil('texto2')}
          {renderBorderEditor('texto2')}
          <p
            className="text-xl px-1"
            style={mergePanfletoFieldStyle(
              layoutStyle?.description ? getTextStyle('description') : { color: '#374151', fontSize: 20 },
              'texto2',
              layoutStyle?.panfletoContent
            )}
          >
            {t.texto2}
          </p>
        </div>
      ) : null}

      {t.texto3 ? (
        <div
          data-panfleto-free-id="texto3"
          className={`relative text-center items-center justify-center ${editOutline}`}
          style={slotStyle('texto3')}
          onPointerDown={beginDragMove('texto3')}
        >
          {renderFieldPencil('texto3')}
          {renderBorderEditor('texto3')}
          <p
            className="text-xl px-1"
            style={mergePanfletoFieldStyle(
              layoutStyle?.description ? getTextStyle('description') : { color: '#374151', fontSize: 20 },
              'texto3',
              layoutStyle?.panfletoContent
            )}
          >
            {t.texto3}
          </p>
        </div>
      ) : null}

      {!hideLinha1 && !!(t.linha1 || '').trim() ? (
        <div
          data-panfleto-free-id="linha1"
          className={`relative ${editOutline}`}
          style={slotStyle('linha1')}
          onPointerDown={beginDragMove('linha1')}
        >
          {renderFieldPencil('linha1')}
          {renderBorderEditor('linha1')}
          <div className="w-full" style={{ textAlign: productNameAlign }}>
            <h3
              key={`ff-linha1-${productAnimTick}`}
              className={
                density === 'display'
                  ? `inline-block font-bold text-5xl sm:text-6xl max-w-full px-1 ${productAnimClass}`
                  : 'inline-block font-bold max-w-full px-1'
              }
              style={mergePanfletoFieldStyle(getTextStyle('productName'), 'linha1', layoutStyle?.panfletoContent)}
            >
              {t.linha1}
            </h3>
          </div>
        </div>
      ) : null}

      {t.linha2 && !hideLinha2 ? (
        <div
          data-panfleto-free-id="linha2"
          className={`relative text-center items-center justify-center ${editOutline}`}
          style={slotStyle('linha2')}
          onPointerDown={beginDragMove('linha2')}
        >
          {renderFieldPencil('linha2')}
          {renderBorderEditor('linha2')}
          <p
            key={`ff-linha2-${productAnimTick}`}
            className={density === 'display' ? `text-4xl sm:text-5xl font-extrabold leading-tight px-1 ${productAnimClass}` : 'font-extrabold leading-tight px-1'}
            style={mergePanfletoFieldStyle(
              layoutStyle?.category ? getTextStyle('category') : { color: '#111827' },
              'linha2',
              layoutStyle?.panfletoContent
            )}
          >
            {t.linha2}
          </p>
        </div>
      ) : null}
      </>
    );

    const productPhotoSlotMarkup =
      panfletoProdutoFotoViaApi && panfletoFotoObjectUrl ? (
        <div
          key={`ff-foto-wrap-${productAnimTick}`}
          data-panfleto-free-id="imagemProduto"
          className={`relative overflow-hidden rounded-lg bg-white/5 ${editOutline}`}
          style={slotStyle('imagemProduto')}
          onPointerDown={beginDragMove('imagemProduto')}
        >
          <img
            key={`panfleto-foto-${promo.id}-${panfletoFotoObjectUrl}`}
            src={panfletoFotoObjectUrl}
            alt=""
            className="h-full w-full object-fill object-center pointer-events-none"
            decoding="async"
          />
          {positionEditMode && onElementPlacementsChange ? (
            <>
              <button
                type="button"
                aria-label="Redimensionar foto (canto inferior direito)"
                className="absolute bottom-0.5 right-0.5 h-4 w-4 cursor-nwse-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: 1, y: 1 })(e);
                }}
              >
                <span className="absolute bottom-0 right-0 h-0.5 w-3 bg-blue-500 rounded-full" />
                <span className="absolute bottom-0 right-0 h-3 w-0.5 bg-blue-500 rounded-full" />
              </button>
              <button
                type="button"
                aria-label="Redimensionar foto (canto inferior esquerdo)"
                className="absolute bottom-0.5 left-0.5 h-4 w-4 cursor-nesw-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: -1, y: 1 })(e);
                }}
              >
                <span className="absolute bottom-0 left-0 h-0.5 w-3 bg-blue-500 rounded-full" />
                <span className="absolute bottom-0 left-0 h-3 w-0.5 bg-blue-500 rounded-full" />
              </button>
              <button
                type="button"
                aria-label="Redimensionar foto (canto superior direito)"
                className="absolute top-0.5 right-0.5 h-4 w-4 cursor-nesw-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: 1, y: -1 })(e);
                }}
              >
                <span className="absolute top-0 right-0 h-0.5 w-3 bg-blue-500 rounded-full" />
                <span className="absolute top-0 right-0 h-3 w-0.5 bg-blue-500 rounded-full" />
              </button>
              <button
                type="button"
                aria-label="Redimensionar foto (canto superior esquerdo)"
                className="absolute top-0.5 left-0.5 h-4 w-4 cursor-nwse-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: -1, y: -1 })(e);
                }}
              >
                <span className="absolute top-0 left-0 h-0.5 w-3 bg-blue-500 rounded-full" />
                <span className="absolute top-0 left-0 h-3 w-0.5 bg-blue-500 rounded-full" />
              </button>
              <button
                type="button"
                aria-label="Redimensionar foto (borda direita)"
                className="absolute right-0 top-1/2 h-16 w-4 -translate-y-1/2 cursor-ew-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: 1, y: 0 })(e);
                }}
              >
                <span className="absolute right-0.5 top-1/2 h-12 w-0.5 -translate-y-1/2 rounded-full bg-blue-500" />
              </button>
              <button
                type="button"
                aria-label="Redimensionar foto (borda esquerda)"
                className="absolute left-0 top-1/2 h-16 w-4 -translate-y-1/2 cursor-ew-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: -1, y: 0 })(e);
                }}
              >
                <span className="absolute left-0.5 top-1/2 h-12 w-0.5 -translate-y-1/2 rounded-full bg-blue-500" />
              </button>
              <button
                type="button"
                aria-label="Redimensionar foto (borda superior)"
                className="absolute top-0 left-1/2 h-4 w-16 -translate-x-1/2 cursor-ns-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: 0, y: -1 })(e);
                }}
              >
                <span className="absolute top-0.5 left-1/2 h-0.5 w-12 -translate-x-1/2 rounded-full bg-blue-500" />
              </button>
              <button
                type="button"
                aria-label="Redimensionar foto (borda inferior)"
                className="absolute bottom-0 left-1/2 h-4 w-16 -translate-x-1/2 cursor-ns-resize pointer-events-auto z-30"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  beginResizeImage({ x: 0, y: 1 })(e);
                }}
              >
                <span className="absolute bottom-0.5 left-1/2 h-0.5 w-12 -translate-x-1/2 rounded-full bg-blue-500" />
              </button>
            </>
          ) : null}
        </div>
      ) : null;

    const imageSlot = <>{productPhotoSlotMarkup}</>;

    const textSlotsBottom = (
      <>
      {t.precoDe && !hidePrecoDe ? (
        <div
          key={`ff-preco-de-wrap-${productAnimTick}`}
          data-panfleto-free-id="precoDe"
          className={`relative flex flex-col items-center justify-center ${editOutline}`}
          style={slotStyle('precoDe')}
          onPointerDown={beginDragMove('precoDe')}
        >
          <div
            style={
              precoDeShellStyle
                ? { ...precoDeShellStyle, lineHeight: 1.25, whiteSpace: 'nowrap', minWidth: 'fit-content' }
                : {
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }
            }
          >
            <div className={pricePairInnerRowClass(precoDeSplitOn, precoDeCaptionLayout, productAnimClass)}>
              <span
                className={precoDeSplitOn ? 'font-medium' : 'text-sm font-medium'}
                style={mergePanfletoPricePairStyle(
                  { fontFamily: priceTypo.fontFamily, fontSize: '0.875rem', color: '#111827', fontWeight: 600 },
                  'precoDe',
                  'label',
                  layoutStyle?.panfletoContent
                )}
              >
                DE
              </span>
              <span
                className="line-through"
                style={mergePanfletoPricePairStyle(
                  precoDeValueBaseStyle,
                  'precoDe',
                  'value',
                  layoutStyle?.panfletoContent
                )}
              >
                {precoDeLabel}
              </span>
            </div>
          </div>
          {renderFieldPencil('precoDe')}
          {renderBorderEditor('precoDe')}
        </div>
      ) : null}

      {t.precoPor && !hidePrecoPor ? (
        <div
          key={`ff-preco-por-wrap-${productAnimTick}`}
          data-panfleto-free-id="precoPorValor"
          className={`relative text-center items-center justify-center flex flex-col gap-2 ${editOutline}`}
          style={slotStyle('precoPorValor')}
          onPointerDown={beginDragMove('precoPorValor')}
        >
          <div
            style={
              precoPorShellStyle
                ? { ...precoPorShellStyle, lineHeight: 1.25, whiteSpace: 'nowrap', minWidth: 'fit-content' }
                : {
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }
            }
          >
            <div className={pricePairInnerRowClass(precoPorSplitOn, precoPorCaptionLayout, productAnimClass)}>
              <span
                className={precoPorSplitOn ? 'font-medium block leading-tight' : 'text-sm font-medium block leading-tight'}
                style={mergePanfletoPricePairStyle(
                  { fontFamily: priceTypo.fontFamily, fontSize: '0.875rem', color: '#111827', fontWeight: 600 },
                  'precoPor',
                  'label',
                  layoutStyle?.panfletoContent
                )}
              >
                POR
              </span>
              <span
                className={
                  precoPorSplitOn ? 'font-bold block leading-tight' : 'text-6xl sm:text-7xl font-bold block leading-tight'
                }
                style={mergePanfletoPricePairStyle(getTextStyle('price'), 'precoPor', 'value', layoutStyle?.panfletoContent)}
              >
                {precoPorLabel || formatBrazilianNumber(promoForTexts.price, layoutStyle?.price?.showCurrency !== false)}
              </span>
            </div>
          </div>
          {renderFieldPencil('precoPor')}
          {renderBorderEditor('precoPor')}
        </div>
      ) : null}

      {!hideQuantidade && (!!(t.quantidade || '').trim() || !!(t.unidadeQuantidade || '').trim()) ? (
        <div
          key={`ff-quantidade-wrap-${productAnimTick}`}
          data-panfleto-free-id="quantidade"
          className={`relative text-center items-center justify-center ${editOutline}`}
          style={slotStyle('quantidade')}
          onPointerDown={beginDragMove('quantidade')}
        >
          {renderFieldPencil('quantidade')}
          {renderBorderEditor('quantidade')}
          <p
            className={`text-xl px-1 ${productAnimClass}`}
            style={mergePanfletoFieldStyle(getTextStyle('unit'), 'quantidade', layoutStyle?.panfletoContent)}
          >
            {t.quantidade}
            {t.unidadeQuantidade ? ` ${t.unidadeQuantidade}` : ''}
          </p>
        </div>
      ) : null}

      {t.validadeTexto && !hideValidade ? (
        <div
          data-panfleto-free-id="validade"
          className={`relative text-center items-center justify-center ${editOutline} max-w-[min(96%,920px)]`}
          style={slotStyle('validade')}
          onPointerDown={beginDragMove('validade')}
        >
          {renderFieldPencil('validade')}
          {renderBorderEditor('validade')}
          <p
            className="text-xl font-semibold px-2 whitespace-pre-line leading-[1.35] [overflow-wrap:anywhere]"
            style={mergePanfletoFieldStyle(
              {
                fontFamily: layoutStyle?.validUntil?.fontFamily ?? layoutStyle?.unit?.fontFamily ?? 'Arial, sans-serif',
                color: vBase.color,
                fontSize: `${vBase.fontSize}px`,
                fontWeight: layoutStyle?.validUntil?.fontWeight ?? 600,
              } as React.CSSProperties,
              'validade',
              layoutStyle?.panfletoContent
            )}
          >
            {t.validadeTexto}
          </p>
        </div>
      ) : null}
      </>
    );

    const editChrome = (
      <>
      {positionEditMode ? (
        <div className="absolute inset-x-0 bottom-2 flex justify-center">
          <div className="rounded bg-black/55 px-2 py-1 text-xs text-white flex items-center gap-2 pointer-events-auto">
            <span>
              Arraste os blocos · use cantos/bordas da foto para redimensionar · Enter para salvar
            </span>
            {panfletoProdutoFotoViaApi && panfletoFotoObjectUrl ? (
              <>
                <button
                  type="button"
                  className="rounded bg-white/15 px-2 py-0.5 hover:bg-white/25"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fitImageToVisibleArea();
                  }}
                >
                  Ajustar foto
                </button>
                <button
                  type="button"
                  className="rounded bg-white/15 px-2 py-0.5 hover:bg-white/25"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    resetImagePlacement();
                  }}
                >
                  Restaurar
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      </>
    );

    return (
      <>
        {useFramerFreeformProductSwap ? (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={promo.id}
              className="absolute inset-0 z-[5] pointer-events-none [&>*]:pointer-events-auto"
              initial={
                motionSlideX
                  ? { opacity: 0, x: 36, y: 0 }
                  : { opacity: 0, x: 0, y: 14 }
              }
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={
                motionSlideX
                  ? { opacity: 0, x: -32, y: 0 }
                  : { opacity: 0, x: 0, y: -12 }
              }
              transition={{ duration: 0.34, ease: [0.22, 0.61, 0.36, 1] }}
            >
              {textSlotsTop}
              {textSlotsBottom}
            </motion.div>
          </AnimatePresence>
        ) : (
          <>
            {textSlotsTop}
            {textSlotsBottom}
          </>
        )}
        {useFramerFreeformProductSwap ? (
          <AnimatePresence mode="wait" initial={false}>
            {productPhotoSlotMarkup ? (
              <motion.div
                key={`panfleto-foto-${promo.id}`}
                className="pointer-events-none absolute inset-0 z-[10] [&>*]:pointer-events-auto"
                initial={fotoMotionInitial}
                animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                exit={fotoMotionExit}
                transition={fotoMotionTransition}
              >
                {productPhotoSlotMarkup}
              </motion.div>
            ) : null}
          </AnimatePresence>
        ) : (
          imageSlot
        )}
        {editChrome}
      </>
    );
  };

  const productShowcaseInner = (
    <div
      className={
        fixedFrameMode
          ? 'w-full h-full min-h-0 p-4'
          : density === 'display'
            ? 'w-full h-full min-h-[72vh] p-4 sm:p-6'
            : 'w-full min-h-[360px] p-4'
      }
    >
      <div
        className="w-full h-full min-h-0 overflow-hidden"
        style={{
          border: `${showcaseCfg.sharedBorderWidth}px solid ${showcaseCfg.sharedBorderColor}`,
          borderRadius: `${showcaseCfg.sharedBorderRadius}px`,
        }}
      >
        <div className="grid w-full h-full min-h-0" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <section
          className="w-full h-full min-h-0 overflow-hidden flex flex-col justify-between"
          style={{
            backgroundColor: showcaseCfg.textPanelBgColor,
            padding: `${showcaseCfg.textPanelPaddingPx}px`,
          }}
        >
          <div className="flex flex-col min-h-0">
            {t.texto1 ? (
              <p
                className={`mb-2 font-medium ${productAnimClass}`}
                style={mergePanfletoFieldStyle(
                  layoutStyle?.description
                    ? { ...getTextStyle('description'), fontWeight: 500 }
                    : { color: '#e5e7eb', fontSize: 14, fontWeight: 500 },
                  'texto1',
                  layoutStyle?.panfletoContent
                )}
              >
                {t.texto1}
              </p>
            ) : null}
            {t.texto2 ? (
              <p
                className={`mb-2 ${productAnimClass}`}
                style={mergePanfletoFieldStyle(
                  layoutStyle?.description ? getTextStyle('description') : { color: '#f3f4f6', fontSize: 20 },
                  'texto2',
                  layoutStyle?.panfletoContent
                )}
              >
                {t.texto2}
              </p>
            ) : null}
            {t.texto3 ? (
              <p
                className={`mb-4 ${productAnimClass}`}
                style={mergePanfletoFieldStyle(
                  layoutStyle?.description ? getTextStyle('description') : { color: '#f3f4f6', fontSize: 20 },
                  'texto3',
                  layoutStyle?.panfletoContent
                )}
              >
                {t.texto3}
              </p>
            ) : null}
            {!hideLinha1 && !!(t.linha1 || '').trim() ? (
              <div className="w-full mb-2" style={{ textAlign: productNameAlign }}>
                <h3
                  className={`inline-block font-bold text-5xl leading-tight max-w-full ${productAnimClass}`}
                  style={mergePanfletoFieldStyle(getTextStyle('productName'), 'linha1', layoutStyle?.panfletoContent)}
                >
                  {t.linha1}
                </h3>
              </div>
            ) : null}
            {t.linha2 && !hideLinha2 ? (
              <p
                className={`text-2xl sm:text-3xl font-extrabold leading-tight ${productAnimClass}`}
                style={mergePanfletoFieldStyle(
                  layoutStyle?.category ? getTextStyle('category') : { color: '#f8fafc' },
                  'linha2',
                  layoutStyle?.panfletoContent
                )}
              >
                {t.linha2}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col items-start gap-2">
            {t.precoDe && !hidePrecoDe ? (
              <div className={pricePairInnerRowClass(precoDeSplitOn, precoDeCaptionLayout, productAnimClass)}>
                {precoDeSplitOn ? (
                  <>
                    <span
                      className={precoDeSplitOn ? 'font-medium' : 'text-sm font-medium'}
                      style={mergePanfletoPricePairStyle(
                        { fontFamily: priceTypo.fontFamily, fontSize: '0.875rem', color: '#f3f4f6', fontWeight: 600 },
                        'precoDe',
                        'label',
                        layoutStyle?.panfletoContent
                      )}
                    >
                      De
                    </span>
                    <span
                      className="font-medium line-through"
                      style={mergePanfletoPricePairStyle(
                        precoDeValueBaseStyle,
                        'precoDe',
                        'value',
                        layoutStyle?.panfletoContent
                      )}
                    >
                      {precoDeLabel}
                    </span>
                  </>
                ) : (
                  <span
                    className="font-medium line-through"
                    style={mergePanfletoFieldStyle(
                      precoDeValueBaseStyle,
                      'precoDe',
                      layoutStyle?.panfletoContent
                    )}
                  >
                    De {precoDeLabel}
                  </span>
                )}
              </div>
            ) : null}
            {t.precoPor && !hidePrecoPor ? (
              <div className={pricePairInnerRowClass(precoPorSplitOn, precoPorCaptionLayout, productAnimClass)}>
                {precoPorSplitOn ? (
                  <>
                    <span
                      className={precoPorSplitOn ? 'font-medium' : 'text-sm font-medium'}
                      style={mergePanfletoPricePairStyle(
                        { fontFamily: priceTypo.fontFamily, fontSize: '0.875rem', color: '#f3f4f6', fontWeight: 600 },
                        'precoPor',
                        'label',
                        layoutStyle?.panfletoContent
                      )}
                    >
                      Por
                    </span>
                    <span
                      className={
                        precoPorSplitOn ? 'font-bold leading-none' : 'text-4xl sm:text-5xl font-bold leading-none'
                      }
                      style={mergePanfletoPricePairStyle(
                        getTextStyle('price'),
                        'precoPor',
                        'value',
                        layoutStyle?.panfletoContent
                      )}
                    >
                      {precoPorLabel || formatBrazilianNumber(promoForTexts.price, layoutStyle?.price?.showCurrency !== false)}
                    </span>
                  </>
                ) : (
                  <span
                    className="text-4xl sm:text-5xl font-bold leading-none"
                    style={mergePanfletoFieldStyle(getTextStyle('price'), 'precoPor', layoutStyle?.panfletoContent)}
                  >
                    {precoPorLabel || formatBrazilianNumber(promoForTexts.price, layoutStyle?.price?.showCurrency !== false)}
                  </span>
                )}
              </div>
            ) : null}
            {!hideQuantidade && (!!(t.quantidade || '').trim() || !!(t.unidadeQuantidade || '').trim()) ? (
              <p
                className={`text-xl ${productAnimClass}`}
                style={mergePanfletoFieldStyle(getTextStyle('unit'), 'quantidade', layoutStyle?.panfletoContent)}
              >
                {t.quantidade}
                {t.unidadeQuantidade ? ` ${t.unidadeQuantidade}` : ''}
              </p>
            ) : null}
            {t.validadeTexto && !hideValidade ? (
              <p
                className={`text-lg font-semibold whitespace-pre-line leading-[1.35] ${productAnimClass}`}
                style={mergePanfletoFieldStyle(
                  {
                    fontFamily: layoutStyle?.validUntil?.fontFamily ?? layoutStyle?.unit?.fontFamily ?? 'Arial, sans-serif',
                    color: vBase.color,
                    fontSize: `${vBase.fontSize}px`,
                    fontWeight: layoutStyle?.validUntil?.fontWeight ?? 600,
                  } as React.CSSProperties,
                  'validade',
                  layoutStyle?.panfletoContent
                )}
              >
                {t.validadeTexto}
              </p>
            ) : null}
          </div>
        </section>

        <section
          className="w-full h-full min-h-0 overflow-hidden flex items-center justify-center"
          style={{
            backgroundColor: showcaseCfg.productPanelBgColor,
            padding: `${showcaseCfg.productPanelPaddingPx}px`,
            borderLeft: `${showcaseCfg.sharedBorderWidth}px solid ${showcaseCfg.sharedBorderColor}`,
          }}
        >
          {hasProductPhoto ? (
            <img
              key={`showcase-foto-${promo.id}-${panfletoFotoObjectUrl}`}
              src={panfletoFotoObjectUrl || undefined}
              alt=""
              className="h-full w-full object-contain"
              decoding="async"
            />
          ) : (
            <div className={`text-sm text-gray-500 ${productAnimClass}`}>Sem foto do produto</div>
          )}
        </section>
        </div>
      </div>
    </div>
  );

  const freeformCanvasShellClass =
    fixedFrameMode
      ? 'relative w-full h-full min-h-0'
      : density === 'display'
        ? 'relative w-full h-full flex-1 min-h-0 min-h-[72vh] sm:min-h-[76vh]'
        : 'relative w-full h-full min-h-[360px]';

  const freeformInner = (
    <div ref={!useShowcaseLayout ? canvasRef : undefined} className={freeformCanvasShellClass}>
      {useShowcaseLayout ? (
        <div className="absolute inset-0 flex min-h-0 min-w-0 items-center justify-center overflow-hidden">
          {/* Não usar calc(100%/s): isso cancela a escala e o bloco continua enchendo o palco. Aqui o miolo encolhe e o fundo do estágio aparece nas bordas. */}
          <div
            ref={canvasRef}
            className="relative h-full w-full min-h-0 min-w-0 max-h-full max-w-full shrink-0"
            data-panfleto-shared-frame-root
            style={{
              transform: `scale(${showcaseFrameScale})`,
              transformOrigin: 'center center',
            }}
          >
            <div className="pointer-events-none absolute inset-0" style={{ isolation: 'isolate' }}>
              <div
                className="h-full w-full min-h-0 overflow-hidden"
                style={{
                  border: `${showcaseCfg.sharedBorderWidth}px solid ${showcaseCfg.sharedBorderColor}`,
                  borderRadius: `${showcaseCfg.sharedBorderRadius}px`,
                  transform: 'translateZ(0)',
                }}
              >
                <div className="grid h-full w-full min-h-0" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div
                    ref={showcaseTextPaneRef}
                    style={{
                      backgroundColor: showcaseCfg.textPanelBgColor,
                      padding: `${showcaseCfg.textPanelPaddingPx}px`,
                    }}
                  />
                  <div
                    ref={showcaseProductPaneRef}
                    style={{
                      backgroundColor: showcaseCfg.productPanelBgColor,
                      padding: `${showcaseCfg.productPanelPaddingPx}px`,
                      borderLeft: `${showcaseCfg.sharedBorderWidth}px solid ${showcaseCfg.sharedBorderColor}`,
                    }}
                  />
                </div>
              </div>
            </div>
            {renderFreeformSlots()}
          </div>
        </div>
      ) : (
        renderFreeformSlots()
      )}
    </div>
  );

  if (useShowcaseLayout) {
    return (
      <>
        {styleClipboardBanner}
        {freeformInner}
      </>
    );
  }
  return (
    <>
      {styleClipboardBanner}
      {useFreeformLayout ? freeformInner : stackInner}
    </>
  );
};
